const API_BASE = process.env.VUE_APP_BASEURL;

const emptyPurchase = () => ({
  quote: null,
  order: null,
  payment: null,
  confirmation: null,
});

const state = {
  products: [],
  catalogMeta: {},
  isLoadingProducts: false,
  catalogError: '',
  purchase: emptyPurchase(),
};

const getters = {
  getRestaurantGiftCardProducts(state) {
    return state.products;
  },
  getRestaurantGiftCardCatalogMeta(state) {
    return state.catalogMeta;
  },
  getRestaurantGiftCardCatalogLoading(state) {
    return state.isLoadingProducts;
  },
  getRestaurantGiftCardCatalogError(state) {
    return state.catalogError;
  },
  getRestaurantGiftCardPurchase(state) {
    return state.purchase;
  },
};

const mutations = {
  SET_PRODUCTS(state, products) {
    state.products = Array.isArray(products) ? products : [];
  },
  SET_CATALOG_META(state, meta) {
    state.catalogMeta = meta && typeof meta === 'object' ? meta : {};
  },
  SET_CATALOG_LOADING(state, value) {
    state.isLoadingProducts = Boolean(value);
  },
  SET_CATALOG_ERROR(state, value) {
    state.catalogError = value || '';
  },
  PATCH_PURCHASE(state, payload) {
    state.purchase = { ...state.purchase, ...payload };
  },
  RESET_PURCHASE(state) {
    state.purchase = emptyPurchase();
  },
};

function apiUrl(path) {
  return `${String(API_BASE || '').replace(/\/$/, '')}/${String(path || '').replace(/^\//, '')}`;
}

function errorMessage(payload, status) {
  if (payload?.message) return payload.message;
  if (payload?.error) return typeof payload.error === 'string' ? payload.error : payload.error.message;
  if (payload?.errors && typeof payload.errors === 'object') {
    const firstError = Object.values(payload.errors).flat().find(Boolean);
    if (firstError) return String(firstError);
  }
  return status ? `Request failed (${status}).` : 'The request could not be completed.';
}

async function parseResponse(response) {
  const contentType = response.headers?.get('content-type') || '';
  const rawBody = await response.text();
  let payload = null;

  if (rawBody) {
    const trimmedBody = rawBody.trim();
    if (
      contentType.includes('application/json')
      || trimmedBody.startsWith('{')
      || trimmedBody.startsWith('[')
    ) {
      try {
        payload = JSON.parse(rawBody);
      } catch {
        payload = { message: rawBody };
      }
    } else {
      payload = { message: rawBody };
    }
  }

  if (!response.ok) {
    return {
      ...(payload && typeof payload === 'object' ? payload : {}),
      __error: true,
      status: response.status,
      message: errorMessage(payload, response.status),
    };
  }

  return payload && typeof payload === 'object' ? payload : { data: payload };
}

async function jsonRequest(path, options = {}) {
  try {
    const response = await fetch(apiUrl(path), {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });
    return parseResponse(response);
  } catch (error) {
    return {
      __error: true,
      status: 0,
      message: error?.message || 'Network request failed.',
    };
  }
}

async function downloadRequest(path) {
  try {
    const response = await fetch(apiUrl(path), {
      headers: { Accept: 'image/svg+xml,application/octet-stream,application/json' },
    });

    if (!response.ok) return parseResponse(response);

    const disposition = response.headers?.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|["'])?([^"';]+)/i);
    return {
      data: {
        blob: await response.blob(),
        filename: filenameMatch ? decodeURIComponent(filenameMatch[1]) : 'restaurant-gift-card-qr.svg',
      },
    };
  } catch (error) {
    return {
      __error: true,
      status: 0,
      message: error?.message || 'The QR could not be downloaded.',
    };
  }
}

const actions = {
  resetRestaurantGiftCardPurchase({ commit }) {
    commit('RESET_PURCHASE');
  },
  async fetchRestaurantGiftCardProducts({ commit }, companyId) {
    commit('SET_CATALOG_LOADING', true);
    commit('SET_CATALOG_ERROR', '');

    try {
      const response = await jsonRequest(`public/restaurant-gift-cards/companies/${encodeURIComponent(companyId)}`);
      if (response.__error) {
        commit('SET_PRODUCTS', []);
        commit('SET_CATALOG_META', {});
        commit('SET_CATALOG_ERROR', response.message);
        return response;
      }

      const products = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.products) ? response.data.products : []);
      const meta = response.meta || (response.data && !Array.isArray(response.data) ? response.data : {});
      commit('SET_PRODUCTS', products);
      commit('SET_CATALOG_META', meta);
      return response;
    } finally {
      commit('SET_CATALOG_LOADING', false);
    }
  },
  async quoteRestaurantGiftCard({ commit }, payload) {
    const response = await jsonRequest(
      `public/restaurant-gift-cards/companies/${encodeURIComponent(payload.company_id)}/quote`,
      {
        method: 'POST',
        body: JSON.stringify({
          company_id: payload.company_id,
          product_id: payload.product_id,
          ...(payload.amount !== undefined && payload.amount !== null && payload.amount !== '' ? { amount: payload.amount } : {}),
        }),
      },
    );
    if (!response.__error && response.data) commit('PATCH_PURCHASE', { quote: response.data });
    return response;
  },
  async createRestaurantGiftCardOrder({ commit }, payload) {
    const { company_id: companyId, ...body } = payload;
    const response = await jsonRequest(
      `public/restaurant-gift-cards/companies/${encodeURIComponent(companyId)}/orders`,
      { method: 'POST', body: JSON.stringify({ company_id: companyId, ...body }) },
    );
    if (!response.__error && response.data) commit('PATCH_PURCHASE', { order: response.data });
    return response;
  },
  async createRestaurantGiftCardPaymentIntent({ commit }, publicToken) {
    const response = await jsonRequest(
      `public/restaurant-gift-cards/orders/${encodeURIComponent(publicToken)}/payment-intent`,
      { method: 'POST', body: JSON.stringify({}) },
    );
    if (!response.__error && response.data) commit('PATCH_PURCHASE', { payment: response.data });
    return response;
  },
  async confirmRestaurantGiftCardPurchase({ commit }, payload) {
    const response = await jsonRequest(
      `public/restaurant-gift-cards/orders/${encodeURIComponent(payload.public_token)}/confirm`,
      {
        method: 'POST',
        body: JSON.stringify({ payment_intent_id: payload.payment_intent_id }),
      },
    );
    if (!response.__error && response.data) commit('PATCH_PURCHASE', { confirmation: response.data });
    return response;
  },
  async getRestaurantGiftCardOrder({ commit }, publicToken) {
    const response = await jsonRequest(`public/restaurant-gift-cards/orders/${encodeURIComponent(publicToken)}`);
    if (!response.__error && response.data) commit('PATCH_PURCHASE', { confirmation: response.data });
    return response;
  },
  resendRestaurantGiftCard(_, publicToken) {
    return jsonRequest(
      `public/restaurant-gift-cards/orders/${encodeURIComponent(publicToken)}/resend`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },
  downloadRestaurantGiftCardQr(_, publicToken) {
    return downloadRequest(`public/restaurant-gift-cards/orders/${encodeURIComponent(publicToken)}/qr`);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
